'use client';

import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import type { SocialSignInPayload } from '@/core/models/auth';
import type { SignInPayload } from '@/core/schemas/sign-in-schema';
import type { SignUpSchema } from '@/core/schemas/sign-up-schema';
import { USER_PROFILE_QUERY_KEY } from '@/core/constants/query-keys';
import useProfile from '@/core/mutations/use-profile-mutations';
import useUserProfile from '@/core/queries/use-user-profile-query';
import {
  getLocalEmailAddress,
  setEmailAddressLocally,
  setEmailLookupDataLocally,
} from '@/core/services/local-storage/email-address';
import { clearPendingSocialAuth } from '@/core/services/social-auth/pending-social-auth';
import { clearLoggedInLocaleCookies } from '@/core/utils/locale/logged-in-locale-cookies';
import { useCookieStorageContext } from '@/ui/context/cookie-storage';

export type SignOutOptions = {
  /** Reload the page after clearing auth state. Default: true */
  reload?: boolean;
  /** Remove orderId and cartItemsCount cookies. Default: true */
  clearCart?: boolean;
};

const useAuth = () => {
  const storage = useCookieStorageContext();
  const { data: user, isFetching: isUserFetching } = useUserProfile();
  const { signin, signup, signinWithIdToken } = useProfile();
  const queryClient = useQueryClient();

  const markAuthenticated = useCallback(async () => {
    await storage.set('hasAuthSession', true);
    await storage.remove('accessToken');
    await storage.remove('refreshToken');
  }, [storage]);

  const invalidateAuthQueries = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: [USER_PROFILE_QUERY_KEY],
    });
    await queryClient.invalidateQueries({
      queryKey: ['bff-catalog-subscriptions'],
    });
  }, [queryClient]);

  // Use `isFetching`, not `isPending`: when there is no auth session the profile
  // query is disabled but stays `pending`, which would otherwise keep status on
  // `loading` forever (e.g. guest checkout skeleton).
  const status = useMemo(() => {
    if (!user && isUserFetching) return 'loading';
    return user ? 'authenticated' : 'unauthenticated';
  }, [user, isUserFetching]);

  const signUp = useCallback(
    async (credentials: SignUpSchema) => {
      await signup(credentials);

      const { password } = credentials.personalData;
      if (!password) {
        throw new Error('Password is required to complete sign up');
      }

      await signin({
        email: credentials.personalData.email,
        password,
      });
      await markAuthenticated();
      await invalidateAuthQueries();
    },
    [signup, signin, markAuthenticated, invalidateAuthQueries],
  );

  const signIn = useCallback(
    async (credentials: SignInPayload) => {
      await signin(credentials);
      await markAuthenticated();
      await invalidateAuthQueries();
    },
    [signin, markAuthenticated, invalidateAuthQueries],
  );

  const signInWithIdToken = useCallback(
    async (idToken: string, authorizationCode: string) => {
      const payload: SocialSignInPayload = { idToken, authorizationCode };
      await signinWithIdToken(payload);
      await markAuthenticated();
      clearPendingSocialAuth();
      await invalidateAuthQueries();
    },
    [signinWithIdToken, markAuthenticated, invalidateAuthQueries],
  );

  const signOut = useCallback(
    async (options?: SignOutOptions) => {
      const { reload = true, clearCart = true } = options ?? {};

      await storage.remove('evaAuthenticationToken');
      await storage.remove('hasAuthSession');
      await storage.remove('accessToken');
      await storage.remove('refreshToken');
      if (clearCart) {
        await storage.remove('orderId');
        await storage.remove('cartItemsCount');
      }
      setEmailAddressLocally(null);
      setEmailLookupDataLocally(null);
      clearPendingSocialAuth();
      clearLoggedInLocaleCookies();
      queryClient.removeQueries({ queryKey: [USER_PROFILE_QUERY_KEY] });
      queryClient.removeQueries({ queryKey: ['bff-catalog-subscriptions'] });
      if (reload) {
        window.location.reload();
      }
    },
    [storage, queryClient],
  );

  const emailAddress = getLocalEmailAddress() || '';

  return {
    status,
    user: user ?? null,
    signUp,
    signIn,
    signInWithIdToken,
    signOut,
    emailAddress,
  };
};

export default useAuth;
